import { DataGrid } from 'src/feature/data-grid/data-grid';
import { useGetChargesQuery } from 'src/core/service/charging-session.service';
import {
  DEFAULT_DATA_GRID_MODEL,
  DEFAULT_PAGE_SIZE,
} from 'src/config/data-grid.config';
import { Snackbar } from 'src/shared/snackbar/snackbar';
import { useTranslation } from 'react-i18next';

import { SortOrder } from '@delmonicos/shared-sdk/libs/emsp/src/libs/core/src/utils/mongo/pagination/models/pagination.model';
import { useState } from 'react';
import { ChargingSessionsFilterStatusEnum } from '@delmonicos/shared-sdk/libs/emsp/src/libs/shared/src/transaction-service/enum/charging-session-filter-state.enum';
import { GetChargingSessionsPaginatedModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/back-office/src/models/charging-sessions/get-charging-session-paginated.model';
import { useDataGridSearchParams } from 'src/feature/data-grid/use-data-grid-search-params/use-data-grid-search-params';
import { DataGridQueryModel } from '@model/data-grid.model';
import { useChargingSessionColumn } from './use-charging-session-column';
import { useChargingSessionFilter } from './use-charging-session-filter';

export function ChargingSessionDataGrid(): React.JSX.Element {
  const { t } = useTranslation();
  const columns = useChargingSessionColumn();
  const filterDefinitionModel = useChargingSessionFilter();

  // FILTER BY URL  ////////////////////////////////////////////////////////////
  const { model: urlModel } = useDataGridSearchParams(DEFAULT_DATA_GRID_MODEL);
  const [model, setModel] = useState<DataGridQueryModel>(urlModel);

  // PAGINATION & FILTERS //////////////////////////////////////////////////////

  const [params, setParams] = useState<GetChargingSessionsPaginatedModel>(
    () => {
      const pageSize =
        urlModel.paginate?.pageSize ??
        DEFAULT_DATA_GRID_MODEL.paginate?.pageSize ??
        DEFAULT_PAGE_SIZE;
      const page =
        urlModel.paginate?.page ?? DEFAULT_DATA_GRID_MODEL.paginate?.page ?? 0;

      return {
        sortField: 'stoppedAt',
        sortOrder: SortOrder.DESC,
        limit: pageSize,
        offset: page * pageSize,
        textSearch: urlModel.textSearch || undefined,
        state:
          (urlModel.filter?.state as ChargingSessionsFilterStatusEnum) ??
          undefined,
        energyDeliveredMin: urlModel.filter?.energyDeliveredMin
          ? Number(urlModel.filter.energyDeliveredMin)
          : undefined,
        energyDeliveryMax: urlModel.filter?.energyDeliveryMax
          ? Number(urlModel.filter.energyDeliveryMax)
          : undefined,
        totalTTCMin: urlModel.filter?.totalTTCMin
          ? Number(urlModel.filter.totalTTCMin)
          : undefined,
        totalTTCMax: urlModel.filter?.totalTTCMax
          ? Number(urlModel.filter.totalTTCMax)
          : undefined,
        pseudo: urlModel.filter?.pseudo?.toString() || undefined,
      };
    },
  );

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const {
    data: paginatedData,
    isFetching,
    isError,
  } = useGetChargesQuery(params);

  const onModelChange = (newModel: DataGridQueryModel) => {
    setModel(newModel);

    setParams((prev) => {
      const pageSize =
        newModel.paginate?.pageSize ?? prev.limit ?? DEFAULT_PAGE_SIZE;
      const page = newModel.paginate?.page ?? 0;

      const sortOrder =
        newModel.sort?.at(0)?.sort === 'asc' ? SortOrder.ASC : SortOrder.DESC;

      return {
        ...prev,
        sortField: newModel.sort?.at(0)?.field ?? prev.sortField ?? 'stoppedAt',
        sortOrder,
        limit: pageSize,
        offset: page * pageSize,
        textSearch: newModel.textSearch || undefined,
        state:
          (newModel.filter?.state as ChargingSessionsFilterStatusEnum) ??
          undefined,
        energyDeliveredMin: newModel.filter?.energyDeliveredMin
          ? Number(newModel.filter.energyDeliveredMin)
          : undefined,
        energyDeliveryMax: newModel.filter?.energyDeliveryMax
          ? Number(newModel.filter.energyDeliveryMax)
          : undefined,
        totalTTCMin: newModel.filter?.totalTTCMin
          ? Number(newModel.filter.totalTTCMin)
          : undefined,
        totalTTCMax: newModel.filter?.totalTTCMax
          ? Number(newModel.filter.totalTTCMax)
          : undefined,
        pseudo: newModel.filter?.pseudo?.toString() || undefined,
      };
    });
  };

  return (
    <>
      <DataGrid
        columns={columns}
        filterDefinitionModel={filterDefinitionModel}
        loading={isFetching}
        rows={paginatedData?.docs ?? []}
        rowCount={paginatedData?.totalDocs ?? 0}
        model={model}
        onModelChange={onModelChange}
      />
      {isError && (
        <Snackbar
          variant="error"
          message={t('error.get-list-charging-session-query')}
        />
      )}
    </>
  );
}
